import styled, { keyframes } from 'styled-components';
import { motion } from 'motion/react';
import {
  mqMediumMin,
  mqLargeMin,
  mqXXLargeMin,
  mqLargeMax,
} from 'styled/tools/media-queries';

export const ProductSection = styled.section<{
  $color: 'seedgreen' | 'snowwhite';
}>`
  background-color: ${(props) =>
    props.$color === 'seedgreen' ? 'var(--fill-brand)' : 'var(--fill-primary)'};
  padding-block: var(--spacing-56);
`;

export const CarouselContainer = styled.div<{
  total: number;
}>`
  --padding: calc(100% - 1440px);

  display: flex;
  margin-block-end: var(--spacing-16);
  flex-wrap: nowrap;
  gap: var(--spacing-16);
  padding-inline: var(--spacing-16);
  position: relative;
  // believe me i tried to tokenize this with 24px but it caused vertical overflow
  padding-block: 26px;

  @media ${mqMediumMin} {
    padding-inline: var(--spacing-24);
  }

  && .card {
    min-width: 70%;

    @media ${mqLargeMin} {
      min-width: 320px;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }

  & .card {
    cursor: pointer;
    flex: 1;
  }

  @media ${mqLargeMin} {
    padding-inline: var(--spacing-32);
    ${(props) =>
      props.total > 4
        ? 'justify-content: flex-start;'
        : 'justify-content: center;'}
  }
  @media ${mqLargeMax} {
    overflow-x: scroll;
  }

  @media (min-width: 1441px) {
    padding-inline: calc(var(--padding) / 2);
  }

  ${(props) =>
    props.total > 2 &&
    `
    overflow-x: scroll;

    & .card {
      min-width: 220px;

      @media ${mqLargeMin} {
        min-width: 320px;
      }
    }
  `}

  ${(props) =>
    props.total <= 3 &&
    `
    & .card {
      min-width: 220px;
    }
  `}
`;

export const ProductGridContainer = styled.div`
  --padding: calc(100% - 1440px);
  display: grid;
  gap: var(--spacing-16);
  grid-template-columns: repeat(2, minmax(10px, 1fr));
  margin-block-end: var(--spacing-16);
  padding-inline: var(--spacing-16);

  @media ${mqMediumMin} {
    padding-inline: var(--spacing-24);
  }

  @media ${mqLargeMin} {
    grid-template-columns: repeat(4, minmax(10px, 1fr));
    padding-top: 24px;
    padding-inline: var(--spacing-32);
  }

  @media ${mqXXLargeMin} {
    padding-inline: calc(var(--padding) / 2);
  }
`;

export const MicroNavInner = styled.div<{ $color: 'seedgreen' | 'snowwhite' }>`
  display: flex;
  background: ${(props) =>
    props.$color === 'seedgreen' ? 'var(--scrim-glass-dark-35)' : '#efefea'};
  border-radius: var(--radius-full);
  padding: 12px;
  position: relative;
  margin-inline: auto;
  overflow-x: scroll;
  width: fit-content;
  max-width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  &.mobile {
    overflow-x: auto;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
`;

export const MicroNav = styled.div`
  margin-block-end: var(--spacing-40);
  overflow: hidden;

  @media ${mqLargeMax} {
    padding-inline: var(--spacing-16);

    @media ${mqMediumMin} {
      padding-inline: var(--spacing-24);
    }
  }
`;

export const MicroNavWrapper = styled.div`
  position: relative;
  border-radius: var(--radius-full);
`;

export const MicroNavButton = styled.button<{
  $color: 'seedgreen' | 'snowwhite';
}>`
  background: transparent;
  border: none;
  padding: 10px;
  color: ${(props) =>
    props.$color === 'seedgreen'
      ? 'var(--text-inverse-primary)'
      : 'var(--text-primary)'};
  cursor: pointer;
  font-family: var(--ff, 'Seed Sans');
  font-size: var(--font-size-350);
  font-weight: 350;
  position: relative;
  align-items: center;
  display: flex;
  z-index: 1;
  gap: var(--spacing-8);
  white-space: nowrap;

  @media ${mqLargeMin} {
    padding: 10px 18px;
  }
`;

export const MicroNavBlobWrapper = styled(motion.div)`
  position: absolute;
  inset: 0;
  height: 100%;
  filter: url(#gooey);
  display: flex;
  overflow-x: scroll;
  padding: 12px;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${mqLargeMin} {
    width: fit-content;
  }

  &.mobile {
    overflow-x: auto;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
`;

const scaleAnim = keyframes`
  10% {
    transform: scale(1.1);
  }
  20% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
`;

export const MicroNavBlob = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  > span {
    aspect-ratio: 1;
    background-color: var(--products-dm-02-dark);
    border-radius: 50%;
    transform: scale(0);
    transition: transform 0.5s;
    width: 32px;
  }

  &.is-visible {
    > span {
      animation: ${scaleAnim} 2s cubic-bezier(0.75, 0, 0.25, 1);
    }
  }
`;

export const MicroNavBg = styled.div<{ $color: 'seedgreen' | 'snowwhite' }>`
  position: absolute;
  top: 12px;
  left: 0;
  width: var(--bg-width);
  height: calc(100% - 24px);
  border-radius: var(--radius-full);
  background-color: ${(props) =>
    props.$color === 'seedgreen'
      ? 'var(--products-dm-02-dark)'
      : 'var(--fill-primary)'};
  transition: all 0.8s;
`;

export const MicroNavBgMobile = styled(motion.div)<{
  $color: 'seedgreen' | 'snowwhite';
}>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-full);
  background-color: ${(props) =>
    props.$color === 'seedgreen' ? 'var(--fill-brand)' : 'var(--fill-primary)'};
`;
